import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PlaylistAdd as PlaylistAddIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';

import PageHeader from '../../../Components/PageHeader';
import CreateAckBatchDialog from './CreateAckBatchDialog';
import { useApiGet } from '../../../utils/httpClient';
import { listAcknowledgementBatches } from '../../../endpoints/donations';
import { getMyPermissions } from '../../../endpoints/usersTs';
import { hasUserApplicationPermission } from '../../../utils/permissions';

const PAGE_SIZE = 25;

function formatDateTime(value) {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MMM D, YYYY h:mm A') : value;
}

export default function DonationAckBatchesList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { data: permissions } = useApiGet(
    ['my-permissions-ack-batches-list'],
    () => getMyPermissions({ international: '1' }),
    { enabled: true }
  );
  const canManage = hasUserApplicationPermission(
    permissions,
    'donations:manage'
  );

  const { data, isFetching } = useApiGet(
    ['donation-ack-batches-list', page],
    () =>
      listAcknowledgementBatches({
        page: page + 1,
        rowsPerPage: PAGE_SIZE,
      }),
    { enabled: canManage, retry: 1, refetchOnMount: 'always' }
  );

  const permissionsLoading = permissions === undefined;
  const listLoading = permissionsLoading || isFetching;

  const rows = data?.data ?? [];
  const total = data?.pagination?.total_count ?? 0;

  if (!canManage && permissions !== undefined) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>
          You do not have permission to view acknowledgement batches.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Acknowledgement batches"
        subtitle="Open or sent mail batches for donation acknowledgement letters."
        leftDom={
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/internal/donations')}
          >
            Back to donations
          </Button>
        }
        rightDom={
          canManage && (
            <Button
              variant="contained"
              startIcon={<PlaylistAddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              New acknowledgement batch
            </Button>
          )
        }
      />

      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {listLoading
              ? 'Loading…'
              : `${total} batch${total === 1 ? '' : 'es'} found.`}
          </Typography>
          {rows.length === 0 && !listLoading ? (
            <Typography variant="body2" color="text.secondary">
              No acknowledgement batches yet. Create one to get started.
            </Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Batch #</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Items</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Sent</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() =>
                          navigate(`/internal/donations/ack-batches/${row.id}`)
                        }
                      >
                        <TableCell>{row.batch_number}</TableCell>
                        <TableCell>
                          {row.sent_at ? (
                            <Chip label="Sent" size="small" color="default" />
                          ) : (
                            <Chip label="Open" size="small" color="primary" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {row.item_count ?? 0}
                        </TableCell>
                        <TableCell>{formatDateTime(row.created_at)}</TableCell>
                        <TableCell>{formatDateTime(row.sent_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
              />
            </>
          )}
        </CardContent>
      </Card>

      <CreateAckBatchDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={(res) => {
          if (res?.batch?.id) {
            navigate(`/internal/donations/ack-batches/${res.batch.id}`);
          }
        }}
      />
    </Box>
  );
}
